import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, Edit2, Trash2 } from 'lucide-react';
import useClientStore from '@/stores/useClientStore';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import Pagination from '@/components/ui/Pagination';

const TYPE_TABS = [
  { value: 'tous', label: 'Tous' },
  { value: 'particulier', label: 'Particulier' },
  { value: 'entreprise', label: 'Entreprise' },
];

export default function ClientsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clients, total, loading, error, fetchClients, deleteClient } = useClientStore();
  const debounceRef = useRef(null);

  const search = searchParams.get('search') || '';
  const type = searchParams.get('type') || 'tous';
  const skip = parseInt(searchParams.get('skip') || '0', 10);
  const limit = parseInt(searchParams.get('limit') || '20', 10);
  const sortBy = searchParams.get('sort_by') || 'nom';
  const sortOrder = searchParams.get('sort_order') || 'asc';

  const buildParams = useCallback(() => {
    const params = { skip, limit, sort_by: sortBy, sort_order: sortOrder };
    if (search) params.search = search;
    if (type !== 'tous') params.type = type;
    return params;
  }, [search, type, skip, limit, sortBy, sortOrder]);

  useEffect(() => {
    fetchClients(buildParams());
  }, [fetchClients, buildParams]);

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === '' || v === 'tous' || v === 0) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      });
      if (!('skip' in updates)) next.delete('skip');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value });
    }, 300);
  };

  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    updateParams({ sort_by: column, sort_order: newOrder });
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client?')) {
      await deleteClient(clientId);
      fetchClients(buildParams());
    }
  };

  const getClientDisplayName = (client) => {
    if (client.type === 'entreprise') {
      const name = client.raison_sociale || 'Entreprise';
      return client.forme_juridique ? `${name} (${client.forme_juridique})` : name;
    }
    return `${client.prenom || ''} ${client.nom || ''}`.trim() || 'Client';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
            <p className="text-slate-600 mt-2">Gérez vos clients et leurs dossiers</p>
          </div>
          <button
            onClick={() => navigate('/clients/new')}
            className="flex items-center gap-2 px-4 py-2 bg-carex-600 text-white rounded-lg hover:bg-carex-700 transition"
          >
            <Plus size={20} />
            Nouveau client
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, email..."
              defaultValue={search}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => updateParams({ type: tab.value })}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                type === tab.value
                  ? 'border-carex-600 text-carex-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && <LoadingSpinner message="Chargement des clients..." />}
        {error && <ErrorMessage message={error} onRetry={() => fetchClients(buildParams())} />}

        {!loading && !error && clients.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <p className="text-slate-500 text-lg">
              {search ? 'Aucun client ne correspond à votre recherche.' : 'Aucun client enregistré.'}
            </p>
          </div>
        )}

        {!loading && !error && clients.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th
                    className="text-left px-6 py-3 font-semibold text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('nom')}
                  >
                    Nom{sortIndicator('nom')}
                  </th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Type</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Email</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Téléphone</th>
                  <th className="text-center px-6 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {clients.map((client) => (
                  <tr key={client.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{getClientDisplayName(client)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-carex-100 text-carex-800">
                        {client.type === 'particulier' ? 'Particulier' : 'Entreprise'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{client.email || '—'}</td>
                    <td className="px-6 py-4 text-slate-600">{client.telephone || '—'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/clients/${client.id}/edit`)}
                          className="p-2 text-carex-600 hover:bg-carex-50 rounded-lg transition"
                          title="Modifier"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              total={total}
              skip={skip}
              limit={limit}
              onChange={(newSkip) => updateParams({ skip: newSkip })}
              onLimitChange={(newLimit) => updateParams({ limit: newLimit })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
